import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../hooks/useNotifications';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { RefreshCw, LogOut, ShopStatus, Wifi, WifiOff, Menu, X, Search, ChevronDown, User, Settings, LogOut as LogOutIcon, Bell, Info, LayoutGrid } from 'lucide-react';
import { spacing, fontSize, fontWeight, borderRadius, shadows } from '../../styles/theme';
import { getKmfcClient, webvonkClient } from '../../lib/supabase';
import {
  fetchShopStatus,
  dispatchShopStatusEvent,
  formatShopStatusLabel,
} from '../lib/shopStatus';
import { useSession } from '../../contexts/SessionContext';
import { useMenu } from '../navigation/MainTabs';
import { useNavigate } from 'react-router-dom';
import formatUsername from '../lib/formatUsername';
import SearchModal from './SearchModal';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;



const TopBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${spacing.lg} 0 ${spacing.xl};
  height: 52px;
  width: 100%;
  background: ${props => props.theme.colors.surface};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  position: relative;
  overflow: visible;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(90deg,
      transparent 0%,
      ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'} 50%,
      transparent 100%);
    pointer-events: none;
  }
  
&::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 80px;
  background: linear-gradient(to bottom,
    rgba(0, 0, 0, 0.08) 0%,
    rgba(0, 0, 0, 0.06) 10%,
    rgba(0, 0, 0, 0.04) 25%,
    rgba(0, 0, 0, 0.02) 45%,
    rgba(0, 0, 0, 0.01) 70%,
    rgba(0, 0, 0, 0.005) 85%,
    transparent 100%
  );
  pointer-events: none;
  opacity: 1;
  filter: blur(1px); // More blur for that Apple glass effect
}
  
  @media (max-width: 768px) {
    padding: 0 ${spacing.md};
  }
`;

const LogoutConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ErrorModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${props => props.theme.colors.surface};
  border-radius: ${borderRadius.lg};
  padding: ${spacing.xl};
  max-width: 400px;
  width: 90%;
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${shadows.lg};
  z-index: 10001;
`;

const ErrorModalTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${spacing.md};
  text-align: center;
`;

const ErrorModalContent = styled.div`
  background: ${props => props.theme.colors.background};
  border-radius: ${borderRadius.md};
  padding: ${spacing.md};
  margin-bottom: ${spacing.lg};
  font-family: monospace;
  font-size: ${fontSize.sm};
  color: #ff6b6b;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
`;

const ErrorModalButtons = styled.div`
  display: flex;
  gap: ${spacing.md};
`;

const ErrorModalButton = styled.button`
  flex: 1;
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  font-size: ${fontSize.md};
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.primary && `
    background: ${props.theme.colors.brand};
    color: white;
    border: none;
    
    &:hover {
      background: ${props.theme.colors.brand}dd;
    }
  `}
  
  ${props => !props.primary && `
    background: ${props.theme.colors.background};
    color: ${props.theme.colors.text};
    border: 1px solid ${props.theme.colors.border};
    
    &:hover {
      background: ${props.theme.colors.border};
    }
  `}
`;

const LogoutConfirmModal = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 16px;
  max-width: 630px;
  width: 100%;
  padding: 24px 20px;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.25);
`;

const LogoutConfirmTitle = styled.h2`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
`;

const LogoutConfirmText = styled.p`
  margin: 0 0 20px 0;
  font-size: 15px;
  color: ${props => props.theme.colors.muted};
`;

const LogoutConfirmButtons = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 12px;
`;

const LogoutConfirmButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  background: ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};

  &.danger {
    background: ${props => props.theme.colors.error};
    color: ${props => props.theme.colors.white};
  }

  &:hover {
    filter: brightness(0.97);
  }
`;

const StandbyModal = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 16px;
  max-width: 560px;
  width: 95%;
  padding: 28px 32px;
  box-shadow: 0 25px 50px rgba(15, 23, 42, 0.3);
`;

const StandbyHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const StandbyDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #f97316;
  box-shadow: 0 0 8px rgba(249, 115, 22, 0.5);
  flex-shrink: 0;
`;

const StandbyTitle = styled.h2`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
`;

const StandbyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
`;

const StandbyCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 10px;
  padding: 14px 16px;
`;

const StandbyCardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.theme.colors.muted};
  margin-bottom: 4px;
`;

const StandbyCardValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.$accent ? '#f97316' : p.theme.colors.text};
`;

const StandbyActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const StandbyBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
  background: ${p => p.$primary ? p.theme.colors.brand : p.theme.colors.border};
  color: ${p => p.$primary ? '#fff' : p.theme.colors.text};

  &:hover { filter: brightness(0.92); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  width: 220px;
  flex-shrink: 0;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  padding: 0 12px;
  height: 32px;
  width: 220px;
  cursor: text;

  input {
    background: none;
    border: none;
    outline: none;
    font-size: 13px;
    color: ${props => props.theme.colors.text};
    width: 100%;
    &::placeholder { color: ${props => props.theme.colors.muted}; opacity: 0.6; }
  }

  svg { color: ${props => props.theme.colors.muted}; opacity: 0.5; flex-shrink: 0; }
`;

const SuperAdminBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 8px;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 100px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255,255,255,0.8);
  transition: all 0.15s;
  margin-left: 28px;
  letter-spacing: -0.01em;
  flex-shrink: 0;
  &:hover { background: rgba(255,255,255,0.12); }
`;

const BadgeDot = styled.div`
  width: 7px; height: 7px; border-radius: 50%;
  background: #f97316; flex-shrink: 0;
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  gap: ${spacing.lg};
  position: absolute;
  right: ${spacing.xl};
  
  @media (max-width: 768px) {
    gap: ${spacing.md};
    right: ${spacing.lg};
  }
`;

const MenuSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  position: absolute;
  right: 0;
  padding-right: ${spacing.xl};
  
  @media (max-width: 768px) {
    padding-right: ${spacing.lg};
  }
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  object-fit: contain;
  display: block;
  filter: ${props => props.theme.mode === 'dark' ? 'brightness(0) invert(1)' : 'brightness(0)'};

  @media (max-width: 768px) {
    height: 40px;
  }

  @media (max-width: 480px) {
    height: 35px;
  }
`;

const statusFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
`;

const dotPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
  70%  { box-shadow: 0 0 0 5px rgba(34,197,94,0); }
  100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
`;

const StatusPill = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 12px 5px 9px;
  border-radius: 999px;
  border: 1px solid ${p => p.$open
    ? 'rgba(34,197,94,0.3)'
    : p.$loading
      ? p.theme.colors.border
      : p.$withReason
        ? 'rgba(239,68,68,0.3)'
        : 'rgba(249,115,22,0.3)'};
  background: ${p => p.$open
    ? p.theme.mode === 'dark' ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.07)'
    : p.$loading
      ? p.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'
      : p.$withReason
        ? p.theme.mode === 'dark' ? 'rgba(239,68,68,0.09)' : 'rgba(239,68,68,0.07)'
        : p.theme.mode === 'dark' ? 'rgba(249,115,22,0.09)' : 'rgba(249,115,22,0.07)'};
  cursor: pointer;
  font-family: inherit;
  transition: background 0.2s ease, border-color 0.2s ease;
  animation: ${statusFadeIn} 0.2s ease;

  &:hover {
    background: ${p => p.$open
      ? 'rgba(34,197,94,0.13)'
      : p.$loading
        ? p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)'
        : p.$withReason
          ? 'rgba(239,68,68,0.13)'
          : 'rgba(249,115,22,0.13)'};
  }
  &:active { transform: scale(0.97); }
`;

const StatusDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.$open ? '#22c55e' : p.$loading ? 'transparent' : p.$withReason ? '#ef4444' : '#f97316'};
  ${p => p.$open && css`animation: ${dotPulse} 2s ease-in-out infinite;`}
  ${p => p.$loading && css`
    border: 2px solid rgba(128,128,128,0.3);
    border-top-color: rgba(128,128,128,0.7);
    animation: ${spin} 0.8s linear infinite;
  `}
`;

const StatusLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: ${p => p.$open ? '#22c55e' : p.$loading
    ? p.theme.colors.muted
    : p.$withReason ? '#ef4444' : '#f97316'};

  @media (max-width: 480px) { display: none; }
`;

const OpenShopBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 2px;
  color: #f97316;
  opacity: 0.7;
  transition: opacity 0.15s ease, transform 0.15s ease;
  flex-shrink: 0;

  &:hover { opacity: 1; }
  &:active { transform: scale(0.9); }
`;

const QuickCloseWrap = styled.div`
  position: relative;
  flex-shrink: 0;
`;

const QuickCloseBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px 4px 8px;
  border-radius: 20px;
  border: 1px solid rgba(239,68,68,0.3);
  background: ${p => p.theme.mode === 'dark' ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.07)'};
  color: #ef4444;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(239,68,68,0.18)' : 'rgba(239,68,68,0.13)'}; }
  &:active { transform: scale(0.97); }
`;

const QuickCloseDropdown = styled.div`
  position: fixed;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.18);
  min-width: 200px;
  overflow: hidden;
  z-index: 9999;
`;

const DropdownItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: none;
  border: none;
  border-bottom: ${p => p.$last ? 'none' : `1px solid ${p.theme.colors.border}`};
  color: ${p => p.$danger ? '#ef4444' : p.theme.colors.text};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.1s ease;

  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'}; }
`;

const DropdownSub = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${p => p.theme.colors.muted};
  display: block;
  margin-top: 1px;
`;

const StatusTextConnection = styled.span`
  font-size: ${fontSize.xs};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.$error ? '#ff6b6b' : '#22c55e'};
`;

const ConnectionText = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  margin-right: ${spacing.xs};
`;

const ConnectionTime = styled.span`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.text};
  
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 4px 10px;
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)'};
    border-color: ${props => props.theme.colors.border};
  }

  &:active { transform: scale(0.97); }
`;


const ConnectionDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.$offline ? '#ef4444' : props.$standby ? '#f97316' : '#22c55e'};
  flex-shrink: 0;
  box-shadow: 0 2px 8px ${props => props.$offline ? 'rgba(239,68,68,0.4)' : props.$standby ? 'rgba(249,115,22,0.4)' : 'rgba(34,197,94,0.4)'};
`;

const DecorativeRefreshIcon = styled(RefreshCw)`
  color: ${props => props.color || props.theme.colors.success};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: all 0.3s ease;
  animation: ${props => props.$spinning ? 'spin 1s linear infinite' : 'none'};
  
  &:hover {
    transform: rotate(90deg);
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.06)'};
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(135deg,
      ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'} 0%,
      transparent 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  &:hover {
    background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'};
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.border};
    transform: translateY(-1px);
    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0) scale(0.98);
    transition: all 0.15s ease;
  }
  
  svg {
    width: 18px;
    height: 18px;
    transition: all 0.3s ease;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
  }
  
  // Tooltip bij hover
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 52px;
    right: -6px;
    background: rgba(15, 23, 42, 0.95);
    backdrop-filter: blur(10px);
    color: white;
    padding: 8px 12px;
    border-radius: 10px;
    border: 1px solid rgba(255,255,255,0.12);
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    z-index: 1000;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    transform: translateX(50%) translateY(-4px);
  }
  
  &:hover::after {
    opacity: 1;
    transform: translateX(50%) translateY(0);
  }
`;

const CompanyDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  background: #111;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 16px;
  padding: 8px;
  min-width: 220px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.7);
  z-index: 1000;
  animation: fadeDropdown 0.2s cubic-bezier(0.16, 1, 0.3, 1) both;
  @keyframes fadeDropdown {
    from { opacity: 0; transform: translateY(-6px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const CompanyDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.06)' : 'transparent'};
  border: 1px solid ${p => p.$active ? 'rgba(255,255,255,0.08)' : 'transparent'};
  border-radius: 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  text-align: left;
  transition: all 0.12s;
  &:hover { background: rgba(255,255,255,0.08); }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const CompanyDropdownIcon = styled.div`
  width: 30px; height: 30px;
  border-radius: 8px;
  background: ${p => p.$color}18;
  border: 1px solid ${p => p.$color}30;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700;
  color: ${p => p.$color};
  flex-shrink: 0;
`;

const CompanyDropdownDot = styled.div`
  width: 6px; height: 6px; border-radius: 50%;
  background: #22c55e;
  margin-left: auto; flex-shrink: 0;
`;

const QUICK_CLOSE_TEMPLATES = [
  { name: 'Algemeen',    dot: '#f97316', title: '' },
  { name: 'Onderhoud',   dot: '#f59e0b', title: 'Website onderhoud' },
  { name: 'Vakantie',    dot: '#8b5cf6', title: 'Vakantie' },
  { name: 'Feestdag',    dot: '#3b82f6', title: 'Feestdag' },
  { name: 'Uitverkocht', dot: '#ef4444', title: 'Uitverkocht' },
  { name: 'Te druk',     dot: '#f97316', title: 'Tijdelijk gesloten' },
  { name: 'Slecht weer', dot: '#64748b', title: 'Gesloten wegens weer' },
  { name: 'Warm weer',   dot: '#f59e0b', title: 'Gesloten wegens hitte' },
  { name: 'Ziekte',      dot: '#ec4899', title: 'Gesloten wegens ziekte' },
  { name: 'Evenement',   dot: '#22c55e', title: 'Gesloten wegens event' },
  { name: 'Technisch',         dot: '#6366f1', title: 'Technische storing' },
  { name: 'Privé',             dot: '#f43f5e', title: 'Gesloten' },
  { name: 'Personeelstekort', dot: '#10b981', title: 'Gesloten wegens personeelstekort' },
];

const SWITCH_COMPANIES = [
  { slug: 'koffiereparatie-geleen', name: 'Koffiereparatie Geleen', initials: 'KG', color: '#ea7211' },
  { slug: 'kmfc-shop', name: 'KMFC Shop', initials: 'KS', color: '#f97316' },
];

const AdminButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 18px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'};
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  color: ${props => props.theme.colors.muted};
  font-size: 18px;
  font-weight: ${fontWeight.medium};
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
      ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'} 0%,
      transparent 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }

  &:hover {
    background: linear-gradient(135deg,
      ${props => props.theme.colors.brand} 0%,
      ${props => props.theme.colors.brand}dd 100%);
    color: white;
    border-color: transparent;
    transform: translateY(-2px) scale(1.05);
    box-shadow: 0 8px 32px rgba(0,0,0,0.15);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
    
    &::before {
      display: none;
    }
  }
  
  // Tooltip bij hover
  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 50px;
    right: -10px;
    background: rgba(15, 23, 42, 0.95);
    backdrop-filter: blur(10px);
    color: white;
    padding: 8px 12px;
    border-radius: 12px;
    border: 1px solid rgba(255,255,255,0.12);
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    pointer-events: none;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1000;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    transform: translateX(50%) translateY(-4px);
  }
  
  &:hover::after {
    opacity: 1;
    transform: translateX(50%) translateY(0);
  }
`;

const AdminButtonIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BellButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 1px solid ${p => p.theme.colors.border};
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${p => p.theme.colors.muted};
  position: relative;
  transition: background 0.15s;
  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; }
`;

const BellBadge = styled.div`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f97316;
  border: 1.5px solid ${p => p.theme.colors.surface};
`;

const InboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
`;

const InboxPanel = styled.div`
  position: fixed;
  width: 340px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.18);
  z-index: 10000;
  overflow: hidden;
  animation: fadeIn 0.15s cubic-bezier(0.16,1,0.3,1);
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-6px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const InboxHeader = styled.div`
  padding: 16px 16px 12px;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const InboxItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  cursor: pointer;
  transition: background 0.12s;
  &:last-child { border-bottom: none; }
  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}; }
`;

const InboxDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$read ? 'transparent' : '#f97316'};
  border: ${p => p.$read ? `1.5px solid ${p.theme.colors.border}` : 'none'};
  flex-shrink: 0;
  margin-top: 5px;
`;

const InboxItemContent = styled.div`
  flex: 1;
`;

const InboxItemTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  margin-bottom: 2px;
`;

const InboxItemBody = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.4;
`;

const InboxItemTime = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 4px;
  opacity: 0.6;
`;

const InboxWrapper = styled.div`
  position: relative;
`;

const InboxMarkAllBtn = styled.button`
  font-size: 12px;
  font-weight: 600;
  color: #f97316;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover { opacity: 0.7; }
`;

const InboxEmpty = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  opacity: 0.5;
`;

function formatRelTime(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'zojuist';
  if (m < 60) return `${m} min geleden`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} uur geleden`;
  return `${Math.floor(h / 24)} dag${Math.floor(h/24) !== 1 ? 'en' : ''} geleden`;
}

const ProfileButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px 4px 4px;
  border-radius: 999px;
  transition: background 0.15s;
  color: ${p => p.theme.colors.text};
  &:hover { background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}; }
`;

const ProfileAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${p => p.theme.colors.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const ProfileName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
`;

const ProfileDropdown = styled.div`
  position: fixed;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  padding: 8px;
  min-width: 200px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.18);
  z-index: 10000;
  animation: fadeIn 0.15s cubic-bezier(0.16,1,0.3,1);
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-6px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
`;

const ProfileDropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  border: none;
  background: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$danger ? '#ef4444' : p.theme.colors.text};
  text-align: left;
  transition: background 0.12s;
  &:hover { background: ${p => p.$danger ? 'rgba(239,68,68,0.08)' : (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)')}; }
  svg { opacity: 0.6; flex-shrink: 0; }
`;

const ProfileDropdownDivider = styled.div`
  height: 1px;
  background: ${p => p.theme.colors.border};
  margin: 6px 0;
`;

const ProfileDropdownHeader = styled.div`
  padding: 8px 12px 10px;
  font-size: 12px;
  color: ${p => p.theme.colors.muted};
`;

const ProfileWrapper = styled.div`
  position: relative;
`;

const TopBar = ({
  connectionError = null,
  style = {},
  adminButton = null,
  hideLogo = false,
  showRealtimeStatus = false,
  showShopStatus = false,
  realtimeStatus = null,
  realtimeError = null,
  lastUpdateTime = null,
  onRealtimeErrorClick = null,
  onRefresh = null,
  onRefreshDoubleClick = null
}) => {
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [shopStatus, setShopStatus] = useState(null);
  const [activeClosure, setActiveClosure] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [profilePos, setProfilePos] = useState({ top: 0, right: 0 });
  const [showInbox, setShowInbox] = useState(false);
  const [inboxPos, setInboxPos] = useState({ top: 0, right: 0 });
  const profileRef = useRef(null);
  const inboxRef = useRef(null);
  const { isAuthenticated, isReady, logout } = useSession();
  const { isOpen, toggleMenu, closeMenu } = useMenu();
  const navigate = useNavigate();

  const storedUser = (() => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}'); } catch { return {}; } })();
  const username = formatUsername(storedUser.username || storedUser.name);
  const initials = username.slice(0, 2).toUpperCase();
  const isKmfcUser = /^kmfc\d+$/i.test(storedUser.username || '');

  const [showQuickClose, setShowQuickClose] = useState(false);
  const [quickClosePos, setQuickClosePos] = useState({ top: 0, right: 0 });
  const quickCloseRef = useRef(null);

  useEffect(() => {
    if (!showQuickClose) return;
    const handler = (e) => {
      if (quickCloseRef.current && !quickCloseRef.current.contains(e.target)) setShowQuickClose(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showQuickClose]);

  const handleOpenShop = async (e) => {
    e.stopPropagation();
    try {
      const kmfcClient = getKmfcClient();
      await kmfcClient.from('shop_settings').update({ is_closed: false, exception_note: null }).not('id', 'is', null);
      setIsShopOpen(true);
      setShopStatus('Restaurant is geopend');
      setActiveClosure(null);
      dispatchShopStatusEvent(true);
    } catch (err) {
      console.error('[TopBar] Failed to open shop:', err);
    }
  };

  const handleQuickClose = async (note) => {
    setShowQuickClose(false);
    try {
      const kmfcClient = getKmfcClient();
      await kmfcClient.from('shop_settings').update({ is_closed: true, exception_note: note || null }).not('id', 'is', null);
      setIsShopOpen(false);
      setShopStatus('Restaurant is gesloten');
      dispatchShopStatusEvent(false);
    } catch (err) {
      console.error('[TopBar] Failed to close shop:', err);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setShowProfileMenu(false);
      if (inboxRef.current && !inboxRef.current.contains(e.target)) setShowInbox(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === '/' && document.activeElement.tagName !== 'INPUT' && document.activeElement.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setShowSearch(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Dubbelklik detection
  const [lastRefreshClick, setLastRefreshClick] = useState(0);
  
  const handleRefreshClick = () => {
    console.log('🔘 TopBar clicked!');
    const now = Date.now();
    const timeDiff = now - lastRefreshClick;
    
    if (timeDiff < 300) { // 300ms voor dubbelklik
      console.log('🔔 Double click detected');
      if (onRefreshDoubleClick) {
        onRefreshDoubleClick();
      }
    } else {
      console.log('👆 Single click detected');
      if (onRefresh) {
        onRefresh();
      }
    }
    
    setLastRefreshClick(now);
  };

  useEffect(() => {
    if (!isReady || !isAuthenticated) {
      setShopStatus('Niet ingelogd');
      return;
    }

    let unsub = null;

    import('../services/DataSyncService').then(({ default: dataSyncService }) => {
      // Haal direct de gecachte waarde op
      const cached = dataSyncService.getData('shopStatus');
      if (cached) {
        setIsShopOpen(cached.isOpen);
        setShopStatus(formatShopStatusLabel(cached.isOpen));
        dispatchShopStatusEvent(cached.isOpen);
        loadActiveClosure();
      } else {
        // Geen cache: laad direct
        fetchShopStatus().then(result => {
          setIsShopOpen(result.isOpen);
          setShopStatus(formatShopStatusLabel(result.isOpen));
          dispatchShopStatusEvent(result.isOpen);
        });
        loadActiveClosure();
      }

      unsub = dataSyncService.subscribe('shopStatus', (status) => {
        if (!status) return;
        setIsShopOpen(status.isOpen);
        setShopStatus(formatShopStatusLabel(status.isOpen));
        dispatchShopStatusEvent(status.isOpen);
      });
    });

    return () => { if (unsub) unsub(); };
  }, [isReady, isAuthenticated]);

  const loadActiveClosure = async () => {
    try {
      const { getKmfcClient } = await import('../../lib/supabase');
      const client = getKmfcClient();
      if (!client) return;
      const now = new Date().toISOString();
      const { data } = await client
        .from('sluitingen')
        .select('title, reason')
        .lte('start_datetime', now)
        .gte('end_datetime', now)
        .order('start_datetime', { ascending: true })
        .limit(1)
        .maybeSingle();
      setActiveClosure(data || null);
    } catch {}
  };

  const refreshShopStatus = async () => {
    if (refreshing || !isAuthenticated) return;
    setRefreshing(true);
    try {
      const { default: dataSyncService } = await import('../services/DataSyncService');
      await dataSyncService.forceSync();
    } catch (error) {
      console.error('Error refreshing shop status:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const performLogout = async () => {
    try {
      // Gebruik de nieuwe logout functie uit SessionContext
      logout();
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: toch naar mijn.webvonk.com
      localStorage.clear();
      window.location.href = '/';
    }
  };

  return (
    <>
    <TopBarContainer style={style}>
      <LeftSection>
        {!hideLogo && (
          <Logo
            src="https://webvonk.com/webvonk-full.png"
            alt="Webvonk"
          />
        )}
      </LeftSection>
      <SearchBar
        style={{ position: 'absolute', left: 'calc(50% - 233px)', transform: 'translateX(-50%)' }}
        onClick={() => setShowSearch(true)}
      >
        <Search size={13} />
        <input placeholder="Zoeken…" readOnly style={{ cursor: 'pointer' }} />
        <span style={{ fontSize: 11, opacity: 0.35, marginLeft: 'auto', flexShrink: 0, fontFamily: 'monospace' }}>/</span>
      </SearchBar>
      
      <RightSection>
        {adminButton && (
          <AdminButton 
            onClick={adminButton.onClick}
            disabled={adminButton.loading}
            data-tooltip="Test order aanmaken"
          >
            {adminButton.loading ? '...' : '+'}
          </AdminButton>
        )}
        
        {showRealtimeStatus && (() => {
          const isOffline = lastUpdateTime && (Date.now() - lastUpdateTime.getTime()) > 10 * 60 * 1000;
          if (!isOffline) return null;
          return (
            <ConnectionStatus onClick={handleRefreshClick}>
              <ConnectionDot $offline={isOffline} />
              <ConnectionTime style={{ color: '#ef4444' }}>Offline</ConnectionTime>
            </ConnectionStatus>
          );
        })()}
        
        {showShopStatus && (
          <>
            <StatusPill
              $open={isShopOpen}
              $loading={shopStatus === null}
              $withReason={!isShopOpen && !!activeClosure}
              onClick={() => { navigate('/KMFC/settings/opening-hours'); if (onRefresh) onRefresh(); }}
            >
              <StatusDot $open={isShopOpen} $loading={shopStatus === null} $withReason={!isShopOpen && !!activeClosure} />
              <StatusLabel $open={isShopOpen} $loading={shopStatus === null} $withReason={!isShopOpen && !!activeClosure}>
                {shopStatus === null
                  ? 'Laden...'
                  : !isShopOpen && activeClosure
                    ? (activeClosure.title.length > 20 ? 'Gesloten met reden' : `Gesloten · ${activeClosure.title}`)
                    : isShopOpen ? 'Restaurant geopend' : 'Restaurant gesloten'}
              </StatusLabel>
            </StatusPill>

          </>
        )}

        <InboxWrapper ref={inboxRef}>
          <BellButton onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setInboxPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
            setShowInbox(p => !p);
            setShowProfileMenu(false);
          }}>
            <Bell size={15} />
            {unreadCount > 0 && <BellBadge />}
          </BellButton>
        </InboxWrapper>

        <ProfileWrapper ref={profileRef}>
          <ProfileButton onClick={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            setProfilePos({ top: r.bottom + 8, right: window.innerWidth - r.right });
            setShowProfileMenu(p => !p);
            setShowInbox(false);
          }}>
            <ProfileAvatar style={isKmfcUser ? { background: '#1a1a1a', padding: 2 } : {}}>
              {isKmfcUser
                ? <img src={`${process.env.PUBLIC_URL}/assets/KMFC_logo.png`} alt="KMFC" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
                : initials}
            </ProfileAvatar>
            <ProfileName>{username}</ProfileName>
            <ChevronDown size={14} style={{ opacity: 0.5, transition: 'transform 0.2s', transform: showProfileMenu ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </ProfileButton>
        </ProfileWrapper>
      </RightSection>



    </TopBarContainer>

    {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}

    {showQuickClose && ReactDOM.createPortal(
      <QuickCloseDropdown style={{ position: 'fixed', top: quickClosePos.top, right: quickClosePos.right, zIndex: 10000 }}>
        <DropdownItem onClick={() => handleQuickClose(null)}>
          <div><div>Nu sluiten</div><DropdownSub>Geen reden</DropdownSub></div>
        </DropdownItem>
        {QUICK_CLOSE_TEMPLATES.map(tpl => (
          <DropdownItem key={tpl.name} onClick={() => handleQuickClose(tpl.title)}>
            <BadgeDot style={{ background: tpl.dot, flexShrink: 0 }} />
            {tpl.name}
          </DropdownItem>
        ))}
        <DropdownItem $last onClick={() => { setShowQuickClose(false); navigate('/KMFC/settings/opening-hours'); }}>
          <div><div>Meer opties...</div><DropdownSub>Openingstijden & sluitingen</DropdownSub></div>
        </DropdownItem>
      </QuickCloseDropdown>,
      document.body
    )}

    {showInbox && ReactDOM.createPortal(
      <InboxPanel style={{ top: inboxPos.top, right: inboxPos.right }}>
        <InboxHeader style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Meldingen</span>
          {unreadCount > 0 && <InboxMarkAllBtn onClick={markAllRead}>Alles gelezen</InboxMarkAllBtn>}
        </InboxHeader>
        {notifications.length === 0
          ? <InboxEmpty>Geen meldingen</InboxEmpty>
          : notifications.slice(0, 15).map(n => (
          <InboxItem key={n.id} onClick={() => {
            markRead(n.id);
            setShowInbox(false);
            if (n.orderId) navigate(`/KMFC/orders/${n.orderId}`);
          }}>
            <InboxDot $read={n.read} />
            <InboxItemContent>
              <InboxItemTitle>{n.title}</InboxItemTitle>
              <InboxItemBody>{n.message}</InboxItemBody>
              <InboxItemTime>{formatRelTime(n.timestamp)}</InboxItemTime>
            </InboxItemContent>
          </InboxItem>
        ))}
      </InboxPanel>,
      document.body
    )}

    {showLogoutConfirm && ReactDOM.createPortal(
      <LogoutConfirmOverlay onClick={() => setShowLogoutConfirm(false)}>
        <LogoutConfirmModal onClick={(e) => e.stopPropagation()}>
          <LogoutConfirmTitle>Uitloggen</LogoutConfirmTitle>
          <LogoutConfirmText>Weet je zeker dat je wilt uitloggen?</LogoutConfirmText>
          <LogoutConfirmButtons>
            <LogoutConfirmButton type="button" onClick={() => setShowLogoutConfirm(false)}>Annuleren</LogoutConfirmButton>
            <LogoutConfirmButton type="button" className="danger" onClick={async () => { setShowLogoutConfirm(false); await performLogout(); }}>Uitloggen</LogoutConfirmButton>
          </LogoutConfirmButtons>
        </LogoutConfirmModal>
      </LogoutConfirmOverlay>,
      document.body
    )}

    {showProfileMenu && ReactDOM.createPortal(
      <ProfileDropdown style={{ top: profilePos.top, right: profilePos.right }}>
        <ProfileDropdownHeader>{username}</ProfileDropdownHeader>
        <ProfileDropdownDivider />
        <ProfileDropdownItem onClick={() => { setShowProfileMenu(false); navigate('/KMFC/settings/account'); }}>
          <User size={15} />
          Account
        </ProfileDropdownItem>
        <ProfileDropdownItem onClick={() => { setShowProfileMenu(false); navigate('/KMFC/settings'); }}>
          <Settings size={15} />
          Instellingen
        </ProfileDropdownItem>
        {(() => { try { return JSON.parse(localStorage.getItem('webvonk_superadmin') || 'null'); } catch { return null; } })() && (
          <ProfileDropdownItem onClick={() => { setShowProfileMenu(false); window.location.href = '/#/company-select'; }}>
            <LayoutGrid size={15} />
            Wissel bedrijf
          </ProfileDropdownItem>
        )}
        <ProfileDropdownDivider />
        <ProfileDropdownItem $danger onClick={() => { setShowProfileMenu(false); setShowLogoutConfirm(true); }}>
          <LogOutIcon size={15} />
          Uitloggen
        </ProfileDropdownItem>
      </ProfileDropdown>,
      document.body
    )}
    </>
  );
}

export default TopBar;